import React from "react"

import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Container,
    Button,
    Divider,
    Avatar,
    Chip,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";

import { useNavigate, useLocation } from "react-router-dom";
import { GetCurrentUser } from "../utils/hooks";
import _ from "lodash";

const routesList = [
    {
        name: "Home",
        path: "/",
        icon: <HomeOutlinedIcon fontSize="small" />,
    },
    {
        name: "Setting",
        path: "/setting",
        icon: <SettingsOutlinedIcon fontSize="small" />,
    },
    {
        name: "Admin",
        path: "/admin",
        icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
    },
    {
        name: "Learn",
        path: "/learn",
        icon: <OndemandVideoOutlinedIcon fontSize="small" />,
    },
    {
        name: "Login",
        path: "/auth",
        icon: <LoginIcon fontSize="small" />,
    },
];

const publicRoutes = [
    {
        name: "Home",
        path: "/",
        icon: <HomeOutlinedIcon fontSize="small" />,
    },
    {
        name: "Result",
        path: "/results",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
    },
    {
        name: "Admit Card",
        path: "/admit",
        icon: <SchoolOutlinedIcon fontSize="small" />,
    },
    {
        name: "Registration",
        path: "/registration",
        icon: <AppRegistrationOutlinedIcon fontSize="small" />,
    },
    {
        name: "Learn",
        path: "/learn",
        icon: <OndemandVideoOutlinedIcon fontSize="small" />,
    },
    {
        name: "Login",
        path: "/auth",
        icon: <LoginIcon fontSize="small" />,
    },
];

const ResponsiveHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [user, setUser] = React.useState(null);
    const [mobileOpen, setMobileOpen] = React.useState(false);

    React.useEffect(() => {
        fetchUserData();
    }, [location.pathname]);

    const fetchUserData = () => {
        const currentUser = GetCurrentUser();

        if (!currentUser) {
            setUser(null);
        } else {
            setUser(currentUser);
        }
    };

    const handleNavigate = (path) => {
        navigate(path);
        setMobileOpen(false);
    };

    const handleCloseMobileMenu = () => {
        setMobileOpen(false);
    };

    /*
     * The lecture portal renders its own header (see Lectures/layout/
     * LearnHeader.js), so the common one steps aside for every /learn route.
     */
    const isLearnSection = location.pathname.startsWith("/learn");

    const userType = _.get(user, "meta_data.user_type", null);

    const availableRoutes = user ? routesList : publicRoutes;

    const filteredRoutes = availableRoutes.filter((route) => {
        // Only owner can access Admin
        if (route.name === "Admin" && userType !== "owner") {
            return false;
        }

        // Students cannot access Setting
        if (route.name === "Setting" && userType === "student") {
            return false;
        }

        return true;
    });

    const isActiveRoute = (path) => {
        if (path === "/") {
            return location.pathname === "/";
        }

        return location.pathname.startsWith(path);
    };

    if (isLearnSection) {
        return null;
    }

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(12px)",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    color: "#111827",
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar
                        disableGutters
                        sx={{
                            minHeight: {
                                xs: 70,
                                md: 82,
                            },
                            justifyContent: "space-between",
                        }}
                    >
                        {/* ================= LOGO ================= */}

                        <Box
                            onClick={() => handleNavigate("/")}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                component="img"
                                src="/platform_logo.png"
                                alt="EduCare"
                                sx={{
                                    height: {
                                        xs: 52,
                                        sm: 58,
                                        md: 64,
                                    },
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>

                        {/* ================= DESKTOP NAV ================= */}

                        <Box
                            sx={{
                                display: {
                                    xs: "none",
                                    md: "flex",
                                },
                                alignItems: "center",
                                gap: 1,
                                ml: "auto",
                            }}
                        >
                            {filteredRoutes.map((route) => {
                                const active = isActiveRoute(route.path);
                                const isLogin = route.path === "/auth";

                                return (
                                    <Button
                                        key={route.name}
                                        onClick={() =>
                                            handleNavigate(route.path)
                                        }
                                        startIcon={route.icon}
                                        variant={
                                            isLogin
                                                ? "contained"
                                                : "text"
                                        }
                                        sx={{
                                            minHeight: 42,
                                            px: 2,
                                            borderRadius: "12px",
                                            textTransform: "none",
                                            fontSize: "0.95rem",
                                            fontWeight: 600,

                                            color: isLogin
                                                ? "#fff"
                                                : active
                                                    ? "#1976d2"
                                                    : "#4b5563",

                                            backgroundColor: isLogin
                                                ? "#1976d2"
                                                : active
                                                    ? "rgba(25,118,210,0.10)"
                                                    : "transparent",

                                            "&:hover": {
                                                backgroundColor: isLogin
                                                    ? "#1565c0"
                                                    : "rgba(25,118,210,0.08)",
                                            },

                                            boxShadow: isLogin
                                                ? "0 4px 12px rgba(25,118,210,0.25)"
                                                : "none",
                                        }}
                                    >
                                        {route.name}
                                    </Button>
                                );
                            })}

                            {/* USER PROFILE */}

                            {user && (
                                <Box
                                    sx={{
                                        ml: 1.5,
                                        pl: 1.5,
                                        borderLeft: "1px solid",
                                        borderColor: "divider",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            bgcolor: "#1976d2",
                                            fontSize: 15,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {String(
                                            _.get(user, "name", "U")
                                        )
                                            .charAt(0)
                                            .toUpperCase()}
                                    </Avatar>

                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                lg: "block",
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {_.get(
                                                user,
                                                "name",
                                                "User"
                                            )}
                                        </Typography>

                                        {userType && (
                                            <Chip
                                                label={userType}
                                                size="small"
                                                sx={{
                                                    mt: 0.5,
                                                    height: 20,
                                                    fontSize: 10,
                                                    textTransform:
                                                        "capitalize",
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* ================= MOBILE ================= */}

                        <IconButton
                            onClick={() => setMobileOpen(true)}
                            sx={{
                                display: {
                                    xs: "flex",
                                    md: "none",
                                },
                                ml: "auto",
                                width: 44,
                                height: 44,
                                borderRadius: "12px",
                                backgroundColor:
                                    "rgba(25,118,210,0.08)",
                                color: "#1976d2",

                                "&:hover": {
                                    backgroundColor:
                                        "rgba(25,118,210,0.15)",
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* ================= MOBILE DRAWER ================= */}

            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleCloseMobileMenu}
                PaperProps={{
                    sx: {
                        width: {
                            xs: "85%",
                            sm: 360,
                        },
                        maxWidth: 380,
                        borderTopLeftRadius: 20,
                        borderBottomLeftRadius: 20,
                    },
                }}
            >
                {/* Drawer Header */}

                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src="/platform_logo.png"
                            alt="EduCare"
                            sx={{
                                height: 52,
                                width: "auto",
                            }}
                        />
                    </Box>

                    <IconButton
                        onClick={handleCloseMobileMenu}
                        sx={{
                            backgroundColor: "#f3f4f6",
                            "&:hover": {
                                backgroundColor: "#e5e7eb",
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                {/* User Information */}

                {user && (
                    <Box
                        sx={{
                            mx: 2,
                            mt: 2,
                            p: 2,
                            borderRadius: 3,
                            background:
                                "linear-gradient(135deg, #1976d2, #42a5f5)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                bgcolor: "rgba(255,255,255,0.25)",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                        >
                            {String(
                                _.get(user, "name", "U")
                            )
                                .charAt(0)
                                .toUpperCase()}
                        </Avatar>

                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 15,
                                }}
                            >
                                {_.get(user, "name", "User")}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    opacity: 0.85,
                                    textTransform: "capitalize",
                                }}
                            >
                                {userType || "User"}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Navigation */}

                <List sx={{ px: 1.5, py: 2 }}>
                    {filteredRoutes.map((route) => {
                        const active = isActiveRoute(route.path);

                        return (
                            <ListItemButton
                                key={route.name}
                                onClick={() =>
                                    handleNavigate(route.path)
                                }
                                sx={{
                                    mb: 0.7,
                                    px: 2,
                                    py: 1.4,
                                    borderRadius: 2.5,

                                    backgroundColor: active
                                        ? "rgba(25,118,210,0.10)"
                                        : "transparent",

                                    color: active
                                        ? "#1976d2"
                                        : "#374151",

                                    "&:hover": {
                                        backgroundColor:
                                            "rgba(25,118,210,0.08)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        mr: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {route.icon}
                                </Box>

                                <ListItemText
                                    primary={route.name}
                                    primaryTypographyProps={{
                                        fontSize: 15,
                                        fontWeight: active ? 700 : 500,
                                    }}
                                />

                                {active && (
                                    <Box
                                        sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            bgcolor: "#1976d2",
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        );
                    })}
                </List>

                {/* Bottom Section */}

                <Box
                    sx={{
                        mt: "auto",
                        p: 2.5,
                    }}
                >
                    <Divider sx={{ mb: 2 }} />

                    <Typography
                        sx={{
                            textAlign: "center",
                            fontSize: 12,
                            color: "text.secondary",
                        }}
                    >
                        EduCare Platform
                    </Typography>

                    <Typography
                        sx={{
                            textAlign: "center",
                            fontSize: 11,
                            color: "text.disabled",
                            mt: 0.5,
                        }}
                    >
                        Education made simple
                    </Typography>
                </Box>
            </Drawer>
        </>
    );
};

export default ResponsiveHeader